// ANETT HORVATH — Render Policy Engine
// Priority: super_admin override > manual_lock > house_mode > membership_access > visibility_status
// Default: DENY

import { getHouseMode, type HouseMode } from '../modules/house-state';
import type { SupportedLang } from '../config/house.config';

export type VisibilityStatus = 'visible' | 'hidden' | 'draft' | 'archived';
export type MembershipAccess = 'public' | 'waitlist' | 'member' | 'inner_circle' | 'admin_only';

export interface RenderableItem {
  visibility_status: VisibilityStatus;
  membership_access: MembershipAccess;
  manual_lock: boolean;
}

export interface ViewerContext {
  role: string; // 'super_admin' | 'house_admin' | 'editor' | 'support' | 'member' | 'public'
  memberStatus?: string; // 'waitlist' | 'active' | 'paused' | 'revoked'
  tier?: string;
  previewAs?: MembershipAccess | null; // admin simulation
}

const ACCESS_HIERARCHY: MembershipAccess[] = ['public', 'waitlist', 'member', 'inner_circle', 'admin_only'];

function accessLevel(access: MembershipAccess): number {
  return ACCESS_HIERARCHY.indexOf(access);
}

function viewerLevel(ctx: ViewerContext): number {
  // If previewing as a role, use that level
  if (ctx.previewAs) return accessLevel(ctx.previewAs);
  if (['super_admin', 'house_admin', 'editor', 'support'].includes(ctx.role)) return accessLevel('admin_only');
  if (ctx.memberStatus === 'active') {
    if (ctx.tier === 'INNER_CIRCLE' || ctx.tier === 'FOUNDING_CIRCLE' || ctx.tier === 'COLLECTOR' || ctx.tier === 'BLACK_CARD') return accessLevel('inner_circle');
    return accessLevel('member');
  }
  if (ctx.memberStatus === 'waitlist') return accessLevel('waitlist');
  return accessLevel('public');
}

// Core render decision
export function canRender(item: RenderableItem, ctx: ViewerContext): boolean {
  // 1) super_admin ALWAYS sees everything (unless previewing)
  if (ctx.role === 'super_admin' && !ctx.previewAs) return true;

  // 2) manual_lock blocks everything except super_admin
  if (item.manual_lock) return false;

  // 3) house mode gate
  const mode = getHouseMode();
  if (mode === 'CLOSED' && ctx.role !== 'super_admin') return false;
  if (mode === 'INNER_CIRCLE_ONLY' && viewerLevel(ctx) < accessLevel('inner_circle')) return false;
  if (mode === 'MEMBERS_ONLY' && viewerLevel(ctx) < accessLevel('member')) return false;
  if (mode === 'WAITLIST_ONLY' && viewerLevel(ctx) < accessLevel('waitlist')) return false;

  // 4) membership_access gate
  if (viewerLevel(ctx) < accessLevel(item.membership_access)) return false;

  // 5) visibility_status gate
  if (item.visibility_status === 'hidden' || item.visibility_status === 'archived') return false;
  if (item.visibility_status === 'draft' && !['super_admin', 'house_admin', 'editor'].includes(ctx.role)) return false;

  return true;
}

// Get viewer context from session
export function buildViewerContext(session: any, member: any, previewAs?: MembershipAccess | null): ViewerContext {
  if (!session) return { role: 'public' };
  return {
    role: session.role || 'public',
    memberStatus: member?.status,
    tier: member?.access_tier,
    previewAs: previewAs || null,
  };
}
